import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField
} from '@mui/material';
import { Controller } from 'react-hook-form';
import {
  ArrowDownIcon,
  DeleteBlackIcon,
  EditBlackIcon
} from '@components/icons/svg-icon.tsx';
import {
  AppAutocompleteWithActionsProps,
  CrudActionEnum
} from '@models/app-autocomplete.model.ts';
import checkIcon from '@assets/icons/check.svg';
import { useTranslation } from 'react-i18next';
import { LanguageEnum } from '@models/language.model.ts';

const AppAutocompleteWithActions: React.FC<
  AppAutocompleteWithActionsProps<any>
> = ({
  name,
  control,
  label,
  options,
  validationProps,
  itemActionHandler,
  selectedList = [],
  onChange,
  placeholder,
  language,
  multiple = true,
  disabled = false
}) => {
  const { t } = useTranslation();

  const [selectedValues, setSelectedValues] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [pendingAddOperation, setPendingAddOperation] = useState(false);
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);

  const fieldRef = useRef<any>(null);
  const previousOptionsRef = useRef<any[]>(options);

  const {
    errors,
    errorMessages,
    validationRules = {},
    getErrorMessage
  } = validationProps || {};

  const getOptionTitle = (option: any): string => {
    if (!option) return '';

    if (typeof option.name === 'string') {
      return option.name;
    }
    if (typeof option.name === 'object' && option.name !== null) {
      return language
        ? option.name?.[language] || option.name?.[LanguageEnum.English]
        : option.name;
    }
    return '';
  };

  const updateSelection = (newValues: any[]) => {
    setSelectedValues(newValues);
    fieldRef.current?.onChange(newValues);
    onChange?.(newValues);
  };

  const handleAddNewItem = (value: string, field: any) => {
    if (value && typeof itemActionHandler === 'function') {
      setPendingAddOperation(true);
      itemActionHandler(value, CrudActionEnum.Add);
      fieldRef.current = field;
      setInputValue('');
    }
  };

  const onEdit = (event: React.MouseEvent<HTMLSpanElement>, option: any) => {
    event.stopPropagation();
    if (option && typeof itemActionHandler === 'function') {
      setPendingEditId(option.id);
      itemActionHandler(option, CrudActionEnum.Edit);
    }
  };

  const onDelete = (event: React.MouseEvent<HTMLSpanElement>, option: any) => {
    event.stopPropagation();
    if (option && typeof itemActionHandler === 'function') {
      itemActionHandler(option, CrudActionEnum.Delete);
    }
  };

  // Sync selectedValues with selectedList prop
  useEffect(() => {
    if (selectedList && selectedList.length > 0) {
      const mappedSelections = selectedList.map((sel: any) => {
        const foundInOptions = options.find((opt) => opt.id === sel.id);
        return foundInOptions || sel;
      });

      if (JSON.stringify(selectedValues) !== JSON.stringify(mappedSelections)) {
        setSelectedValues(mappedSelections);
      }
    } else if (selectedValues.length > 0) {
      setSelectedValues([]);
    }
  }, [selectedList, options]);

  // Handle new item added - detect by comparing options
  useEffect(() => {
    if (pendingAddOperation && fieldRef.current) {
      const previousIds = new Set(
        previousOptionsRef.current.map((opt) => opt.id)
      );
      const newItem = options.find((opt) => !previousIds.has(opt.id));

      if (newItem) {
        const updatedSelection = multiple
          ? [...selectedValues, newItem]
          : [newItem];
        updateSelection(updatedSelection);
        setPendingAddOperation(false);
      }
    }
    previousOptionsRef.current = options;
  }, [options]);

  // Handle edited item - update selection if it was selected
  useEffect(() => {
    if (pendingEditId) {
      const editedItem = options.find((opt) => opt.id === pendingEditId);

      if (editedItem) {
        const wasSelected = selectedValues.some(
          (item) => item.id === pendingEditId
        );

        if (wasSelected && fieldRef.current) {
          const updatedSelection = selectedValues.map((item) =>
            item.id === pendingEditId ? editedItem : item
          );
          updateSelection(updatedSelection);
        }
        setPendingEditId(null);
      }
    }
  }, [options, pendingEditId]);

  // Clean up selections when options are deleted
  useEffect(() => {
    if (selectedValues.length > 0 && options.length > 0) {
      const validSelections = selectedValues.filter((sel) =>
        options.some((opt) => opt.id === sel.id)
      );

      if (validSelections.length !== selectedValues.length) {
        updateSelection(validSelections);
      }
    }
  }, [options]);

  return (
    <FormControl
      error={!!errors?.[name]}
      sx={{ minWidth: 320, width: '100%' }}
      disabled={disabled}
    >
      <FormLabel>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field }) => {
          // Store field ref for async operations
          if (!fieldRef.current) {
            fieldRef.current = field;
          }

          // Don't show "+Add" option when multiple=false and there's already a selected value
          const hasSelectedValue = selectedValues.length > 0;
          const shouldShowAddOption =
            inputValue &&
            !options.some(
              (opt) =>
                getOptionTitle(opt)?.toLowerCase() === inputValue.toLowerCase()
            ) &&
            // When single select mode, don't allow adding if already has selection
            (multiple || !hasSelectedValue);

          const displayOptions = [
            ...options,
            ...(shouldShowAddOption
              ? [{ id: '__add__', name: inputValue }]
              : [])
          ];

          return (
            <Autocomplete
              multiple={multiple}
              disabled={disabled}
              disableCloseOnSelect={multiple}
              popupIcon={<ArrowDownIcon sx={{ width: '18px !important' }} />}
              noOptionsText={t('no_options_text')}
              options={displayOptions}
              getOptionLabel={(option) =>
                option.id === '__add__'
                  ? option.name
                  : getOptionTitle(option) || ''
              }
              isOptionEqualToValue={(option, value) => {
                // Handle primitives (strings, numbers, etc.)
                if (
                  typeof option !== 'object' ||
                  option === null ||
                  typeof value !== 'object' ||
                  value === null
                ) {
                  return option === value;
                }
                // Handle objects with id property
                return option.id === value.id;
              }}
              value={multiple ? selectedValues : selectedValues[0] || null}
              inputValue={inputValue}
              onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
              onChange={(_, newValue) => {
                if (multiple) {
                  const valueArray = newValue as any[];
                  const lastSelected = valueArray[valueArray.length - 1];

                  if (lastSelected?.id === '__add__') {
                    handleAddNewItem(getOptionTitle(lastSelected), field);
                    return;
                  }

                  updateSelection(valueArray);
                } else {
                  const singleValue = newValue as any;

                  if (singleValue?.id === '__add__') {
                    handleAddNewItem(getOptionTitle(singleValue), field);
                    return;
                  }

                  updateSelection(singleValue ? [singleValue] : []);
                }
              }}
              onBlur={() => field.onBlur()}
              renderOption={(props, option) => {
                const isAddOption = option.id === '__add__';
                const isSelected = selectedValues?.some(
                  (selected) => selected?.id === option?.id
                );

                return (
                  <li
                    {...props}
                    key={option.id}
                    className={`relative flex items-center pr-2 group border-none outline-none focus:outline-none h-[40px] cursor-pointer ${isSelected ? 'text-[var(--primary)] font-medium hover:bg-gray-100' : 'hover:bg-gray-100'}`}
                    style={{ overflow: 'visible' }}
                  >
                    <span className="pl-6 flex items-center gap-2 w-full">
                      {isAddOption ? (
                        <span className="text-[var(--primary)]">+Add</span>
                      ) : (
                        <>
                          {isSelected && (
                            <img
                              src={checkIcon}
                              alt="Check"
                              className="absolute left-[5px] top-1/2 transform -translate-y-1/2 w-3 h-3"
                            />
                          )}
                          {getOptionTitle(option)}
                        </>
                      )}
                    </span>

                    {!isAddOption && (
                      <div className="hidden group-hover:flex gap-2 ml-auto">
                        <span
                          className="cursor-pointer bg-white rounded-4xl p-1"
                          onClick={(e) => onEdit(e, option)}
                        >
                          <EditBlackIcon />
                        </span>
                        <span
                          className="cursor-pointer bg-white rounded-4xl p-1"
                          onClick={(e) => onDelete(e, option)}
                        >
                          <DeleteBlackIcon />
                        </span>
                      </div>
                    )}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  error={!!errors?.[name]}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const value = params.inputProps.value;
                      // Don't allow adding via Enter when single select and already has selection
                      const canAdd = multiple || !hasSelectedValue;
                      if (typeof value === 'string' && value.trim() && canAdd) {
                        e.preventDefault();
                        handleAddNewItem(value, field);
                      }
                    }
                  }}
                />
              )}
            />
          );
        }}
      />
      <FormHelperText>
        {errors?.[name] &&
          getErrorMessage?.({
            errors,
            fieldName: name,
            errorMessages,
            validationRules
          })}
      </FormHelperText>
    </FormControl>
  );
};

export default AppAutocompleteWithActions;
